import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CHART_AXIS_TICK, CHART_INK, CHART_MARKS, CHART_SERIES } from "./chartTheme";
import { ChartTooltip } from "./ChartTooltip";

export interface CategoryBarDatum {
  key: string;
  label: string;
  value: number;
  /** Overrides the single-series colour — only for an ordered scale (age bands, states). */
  color?: string;
}

interface CategoryBarChartProps {
  data: CategoryBarDatum[];
  /** How a value reads in the label and the tooltip (counts, money, quantities). */
  format?: (value: number) => string;
  /** Sits beside the value in the tooltip, e.g. "requests". */
  valueName?: string;
  /** Width reserved for the category names on the left. */
  labelWidth?: number;
}

const ROW_HEIGHT = 30;
const AXIS_BAND = 28;

/**
 * Horizontal bars for "compare these categories". Horizontal because the categories are
 * named things (a status, a site, an item) and their names need room to be read.
 *
 * One measure means one colour: shading each bar by its own size would double-encode the
 * length the reader is already looking at and burn the only free channel the chart has.
 * An ordered scale (age bands) passes explicit `color`s instead, stepped light to dark.
 */
export function CategoryBarChart({ data, format = (v) => String(v), valueName, labelWidth = 130 }: CategoryBarChartProps) {
  const height = data.length * ROW_HEIGHT + AXIS_BAND;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 48, bottom: 0, left: 0 }} barCategoryGap={CHART_MARKS.gap}>
        <CartesianGrid horizontal={false} stroke={CHART_INK.gridline} />
        <XAxis
          type="number"
          tick={CHART_AXIS_TICK}
          tickFormatter={format}
          axisLine={{ stroke: CHART_INK.axis }}
          tickLine={false}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          width={labelWidth}
          tick={{ ...CHART_AXIS_TICK, fill: CHART_INK.secondary }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          cursor={{ fill: CHART_INK.gridline, fillOpacity: 0.4 }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <ChartTooltip
                rows={[
                  {
                    color: (payload[0].payload as CategoryBarDatum).color ?? CHART_SERIES[0],
                    label: valueName ? `${(payload[0].payload as CategoryBarDatum).label} · ${valueName}` : (payload[0].payload as CategoryBarDatum).label,
                    value: format(payload[0].value as number),
                  },
                ]}
              />
            ) : null
          }
        />
        <Bar dataKey="value" radius={CHART_MARKS.barRadius} barSize={CHART_MARKS.barSize} isAnimationActive={false}>
          {/* The value rides the bar's tip rather than a second axis — and it is what
              keeps the lighter palette steps readable without leaning on colour. */}
          <LabelList
            dataKey="value"
            position="right"
            formatter={(v) => (typeof v === "number" ? format(v) : "")}
            style={{ fill: CHART_INK.secondary, fontSize: 11 }}
          />
          {/* One Cell per bar so an ordered scale can step its colour; with no explicit
              colour every bar is slot 1, which is the single-series default. */}
          {data.map((d) => (
            <Cell key={d.key} fill={d.color ?? CHART_SERIES[0]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
